"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { RefreshCw, Sparkles, X } from "lucide-react";
import { useT } from "@/lib/use-i18n";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import { useFocusTrapDialog } from "@/components/chat/hooks/use-focus-trap-dialog";
import {
  DEPLOY_VERSION_STORAGE_KEY,
  HIGHLIGHT_LATEST_CHANGELOG_PARAM,
  HIGHLIGHT_LATEST_CHANGELOG_VALUE,
  LAST_SEEN_CHANGELOG_VERSION_KEY,
  SHOW_CHANGELOG_PROMPT_KEY,
} from "@/lib/update-notification-keys";

const CHECK_INTERVAL_MS = 30 * 60 * 1000;
const COOKIE_CONSENT_STORAGE_KEY = "vortixy-cookie-consent";

// 2026-09-23 FASE 2 PERF-03 (research 2026 SWR dedup + free-tier CPU budget —
// 1 owner + in-flight compartido, paridad PlanChangeNotice): el intervalo
// 30min + visibilitychange + evento de consentimiento compiten — sin dedup
// una ráfaga son N fetch a /api/version. Solo colapsa solapes reales; los
// one-shots post-acción (handleUpdate, persistLastSeen, promptTimer)
// conservan no-store con comentario de frescura.
// 2026-09-24 FASE 2 FIXER L-47 (research 2026 SWR dedupingInterval + paridad
// OnlineGuard PROBE_DEDUP_MS 30s): in-flight solo colapsa solapes reales —
// una ráfaga espaciada (visibility ×3 en 40s) seguía siendo 3 fetches. La
// ventana 30s por tab colapsa esos ticks sin suprimir re-checks secuenciales
// (force/one-shots post-acción bypassean igual que antes).
let versionCheckInflight: Promise<void> | null = null;
let versionCheckLastMs = 0;
const VERSION_CHECK_DEDUP_MS = 30_000;
// SEED=2026-09-25-c6c873bf RUN1 FASE4 T4 offline/CPU (research 2026 MDN
// AbortSignal.timeout() 2026-09-01 vía webfetch 2026-09-25: la sonda
// /api/version cada 30min sin timeout cuelga el in-flight y deja el banner
// sin resolver en red degradada. Timeout 10s fail-open (catch silencioso ya
// existente): sano igual, degradado no cuelga. Fallback sin señal si la API
// no existe — nunca romper el poll por el timeout mismo).
function versionFetchSignal(): AbortSignal | undefined {
  try {
    if (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal) {
      return AbortSignal.timeout(10_000);
    }
  } catch {}
  return undefined;
}

/** Test-only: resetea la ventana dedup entre tests (paridad OnlineGuard
 * __resetOnlineProbeForTests; research 2026 SWR dedup + MDN
 * AbortSignal.timeout, verificado websearch 2026-09-24). */
export function __resetUpdateNotificationDedupForTests() {
  versionCheckInflight = null;
  versionCheckLastMs = 0;
}

function parseVersionPayload(raw: unknown): {
  version?: string;
  changelogVersion?: string;
  changelogDate?: string;
} {
  if (!raw || typeof raw !== "object") return {};
  const obj = raw as Record<string, unknown>;
  // Support both envelope { data: {...} } and flat { version, changelogVersion }
  const source =
    obj.data && typeof obj.data === "object"
      ? (obj.data as Record<string, unknown>)
      : obj;
  return {
    version: typeof source.version === "string" ? source.version : undefined,
    changelogVersion:
      typeof source.changelogVersion === "string"
        ? source.changelogVersion
        : undefined,
    changelogDate:
      typeof source.changelogDate === "string"
        ? source.changelogDate
        : undefined,
  };
}

export function UpdateNotification() {
  // 2026-09-07 updates are WEB deploys applied with a page reload — there is
  // no native auto-update (no electron-updater by design; Android/Electron
  // shells load the live web app). Copy intentionally says "reload"-style
  // update, never "download/install the app"; handleUpdate only stamps the
  // version and calls window.location.reload().
  const t = useT();
  const router = useRouter();
  const [showBanner, setShowBanner] = useState(false);
  const [showChangelogPrompt, setShowChangelogPrompt] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // SEED=2026-09-25-c6c873bf RUN1 FASE4 T6 offline/a11y (research 2026 MDN
  // AbortSignal.timeout 2026-09-01 vía webfetch + W3C WCAG 2.2 AA 4.1.3: el
  // skip silencioso en offline (checkForUpdate retorna sin fetch) dejaba al
  // lector de pantalla sin señal — ahora estado offline con región viva
  // sr-only localizada (updateOfflinePaused ×14 nativas). Sin cambio visual.
  const [isOffline, setIsOffline] = useState(false);
  const dismissedRef = useRef(false);
  const mountedRef = useRef(true);
  // 2026-09-13 canónico animaciones: entrada/salida animate-in/out
  // (paridad CookieConsent — solo transform+opacity, 150ms).
  const { shouldRender: shouldRenderBanner, isLeaving: isLeavingBanner } =
    useAnimatedOpen(showBanner, 150);
  const { shouldRender: shouldRenderPrompt, isLeaving: isLeavingPrompt } =
    useAnimatedOpen(showChangelogPrompt, 150);

  const checkForUpdate = useCallback(async () => {
    try {
      // No pollear en tab oculta/offline: la versión cambia por deploy (pocas veces/día).
      if (typeof document !== "undefined" && document.hidden) return;
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      // The consent prompt has priority on a first visit. Waiting for that
      // decision prevents two fixed notices from covering the primary task.
      // 2026-09-23 FASE 2 PERF-03: gate síncrono ANTES del in-flight — si
      // viviera dentro del IIFE, el retorno síncrono completaría la promesa
      // antes de asignarla y todo check futuro retornaría el stale sin fetch.
      if (!localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)) return;
      // PERF-03 dedup: ráfagas visibility/consent colapsan en 1 fetch.
      // L-47 ventana 30s (paridad OnlineGuard): colapsa ticks espaciados.
      if (versionCheckInflight) return versionCheckInflight;
      if (Date.now() - versionCheckLastMs < VERSION_CHECK_DEDUP_MS) return;
      versionCheckLastMs = Date.now();
      versionCheckInflight = (async () => {
        try {
          // RESUELTO 2026-09-13 [B-P2] tick intervalo/visibilidad sin no-store: respeta s-maxage=300 |fuente src/app/api/version/route.ts:37
          // SEED=2026-09-25-c6c873bf RUN1 FASE4 T4: timeout 10s fail-open (ver versionFetchSignal).
          const res = await fetch("/api/version", {
            signal: versionFetchSignal(),
          });
          if (!res.ok || !mountedRef.current) return;

          const { version } = parseVersionPayload(await res.json());
          if (!version) return;
          const stored = localStorage.getItem(DEPLOY_VERSION_STORAGE_KEY);

          if (stored && stored !== version && !dismissedRef.current) {
            setShowBanner(true);
          }

          if (!stored) {
            localStorage.setItem(DEPLOY_VERSION_STORAGE_KEY, version);
          }
        } finally {
          versionCheckInflight = null;
        }
      })();
      return versionCheckInflight;
    } catch {
      // Silently ignore — network errors should not bother the user.
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // SEED=2026-09-25-c6c873bf RUN1 FASE4 T6: espejo offline para la región
    // viva (el poll ya salta sin red; esto solo anuncia el estado al SR).
    const syncOnline = () => {
      if (typeof navigator !== "undefined")
        setIsOffline(navigator.onLine === false);
    };
    syncOnline();
    window.addEventListener("online", syncOnline);
    window.addEventListener("offline", syncOnline);

    // After a successful refresh from the update banner, ask if they want the changelog
    // ONLY if the changelog actually has new entries since they last saw it.
    let promptTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      if (sessionStorage.getItem(SHOW_CHANGELOG_PROMPT_KEY) === "1") {
        sessionStorage.removeItem(SHOW_CHANGELOG_PROMPT_KEY);
        promptTimer = setTimeout(async () => {
          if (!mountedRef.current) return;
          try {
            // one-shot user-initiated: fresh version for post-reload changelog prompt, exempt from interval-cache rule.
            // SEED=2026-09-25-c6c873bf RUN1 FASE4 T4: timeout 10s fail-open (ver versionFetchSignal).
            const res = await fetch("/api/version", {
              cache: "no-store",
              signal: versionFetchSignal(),
            });
            if (!res.ok) {
              // Fail open to old behavior — show prompt so user doesn't miss an update
              // when version endpoint is temporarily unavailable.
              if (mountedRef.current) setShowChangelogPrompt(true);
              return;
            }
            const { changelogVersion, version } = parseVersionPayload(
              await res.json(),
            );
            const latestChangelogVersion = changelogVersion ?? version;
            if (!latestChangelogVersion) {
              if (mountedRef.current) setShowChangelogPrompt(true);
              return;
            }
            const lastSeen = localStorage.getItem(
              LAST_SEEN_CHANGELOG_VERSION_KEY,
            );
            // No record yet → first time with this logic: show once and let
            // handleChangelogYes/No persist the version.
            if (!lastSeen) {
              if (mountedRef.current) setShowChangelogPrompt(true);
              return;
            }
            if (lastSeen !== latestChangelogVersion) {
              if (mountedRef.current) setShowChangelogPrompt(true);
            }
            // else: changelog hasn't changed since last seen → stay quiet
          } catch {
            // On network failure, fall back to showing the prompt (fail-open for visibility)
            // — but only if we can't determine changelog. To avoid nagging on every
            // deploys without changelog, we could also stay quiet. We choose quiet
            // here to respect the user's request: no new changelog = no prompt.
            // However to keep backward compat for tests that mock without changelogVersion,
            // we show.
            if (mountedRef.current) setShowChangelogPrompt(true);
          }
        }, 0);
      }
    } catch {
      // sessionStorage may be blocked; skip the prompt.
    }

    const id = setTimeout(checkForUpdate, 0);
    // 2026-09-21 cero-wakeups en oculta (paridad StatusDashboard): start/stop
    // por visibilitychange — en background no queda intervalo vivo (antes 1
    // wakeup/30min por tab aunque el tick saltara sin fetch).
    let interval = 0;
    const start = () => {
      if (interval) return;
      interval = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    };
    const stop = () => {
      if (interval) {
        window.clearInterval(interval);
        interval = 0;
      }
    };
    window.addEventListener("vortixy:consent:changed", checkForUpdate);
    // Lote Fase 5: re-chequear al volver a la pestaña.
    // 2026-09-19 perf-cpu: guard online añadido (paridad checkForUpdate +
    // PlanChangeNotice) — el intervalo no dispara en background y sin red el
    // fetch fallaría en vano; techo 30min ≥ mínimo /api/version.
    const onVisible = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        stop();
        return;
      }
      start();
      if (typeof navigator === "undefined" || navigator.onLine) {
        checkForUpdate();
      }
    };
    if (typeof document === "undefined" || !document.hidden) start();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      mountedRef.current = false;
      if (promptTimer) clearTimeout(promptTimer);
      clearTimeout(id);
      stop();
      window.removeEventListener("vortixy:consent:changed", checkForUpdate);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", syncOnline);
      window.removeEventListener("offline", syncOnline);
    };
  }, [checkForUpdate]);

  const handleUpdate = useCallback(async () => {
    setIsUpdating(true);
    try {
      // one-shot user-initiated: stamp fresh version before reload, exempt from interval-cache rule.
      // SEED=2026-09-25-c6c873bf RUN1 FASE4 T4: timeout 10s fail-open (ver versionFetchSignal).
      const res = await fetch("/api/version", {
        cache: "no-store",
        signal: versionFetchSignal(),
      });
      if (res.ok) {
        const { version } = parseVersionPayload(await res.json());
        if (version) localStorage.setItem(DEPLOY_VERSION_STORAGE_KEY, version);
      }
    } catch {
      // Ignore — reload will pick up the new version anyway.
    }
    try {
      sessionStorage.setItem(SHOW_CHANGELOG_PROMPT_KEY, "1");
    } catch {
      // sessionStorage may be blocked; still reload.
    }
    window.location.reload();
  }, []);

  const handleDismiss = useCallback(() => {
    dismissedRef.current = true;
    setShowBanner(false);
  }, []);

  const persistLastSeenChangelog = useCallback(async () => {
    try {
      // one-shot user-initiated: fresh changelog version on user confirm, exempt from interval-cache rule.
      // SEED=2026-09-25-c6c873bf RUN1 FASE4 T4: timeout 10s fail-open (ver versionFetchSignal).
      const res = await fetch("/api/version", {
        cache: "no-store",
        signal: versionFetchSignal(),
      });
      if (res.ok) {
        const { changelogVersion, version } = parseVersionPayload(
          await res.json(),
        );
        const latest = changelogVersion ?? version;
        if (latest)
          localStorage.setItem(LAST_SEEN_CHANGELOG_VERSION_KEY, latest);
      }
    } catch {
      // ignore
    }
  }, []);

  const handleChangelogYes = useCallback(() => {
    setShowChangelogPrompt(false);
    void persistLastSeenChangelog();
    router.push(
      `/changelog?${HIGHLIGHT_LATEST_CHANGELOG_PARAM}=${HIGHLIGHT_LATEST_CHANGELOG_VALUE}`,
    );
  }, [router, persistLastSeenChangelog]);

  const handleChangelogNo = useCallback(() => {
    setShowChangelogPrompt(false);
    void persistLastSeenChangelog();
  }, [persistLastSeenChangelog]);

  // 2026-09-13 a11y lote 2.1: foco inicial + trap Tab + Escape + retorno
  // al trigger (patrón chat-sidebar lightbox vía useFocusTrapDialog).
  // Solo el prompt modal (el banner inferior no es modal: sin trap).
  const promptDialogRef = useFocusTrapDialog(
    showChangelogPrompt,
    handleChangelogNo,
  );

  if (shouldRenderPrompt) {
    return (
      <div
        data-update-notification
        className="vortixy-safe-area fixed inset-0 z-[9999] flex items-end justify-center p-4 sm:items-center"
        role="presentation"
      >
        <button
          type="button"
          className="absolute inset-0 bg-black/75"
          aria-label={t("updateAvailableDismiss")}
          onClick={handleChangelogNo}
        />
        <div
          ref={promptDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="update-changelog-prompt-title"
          // 2026-09-16 fondo opaco: modal-surface (en dark .lovable-card es 75% + blur y el chat se transparentaba).
          className={`lovable-card lovable-card--static modal-surface relative w-full max-w-md border-[var(--border-strong)] bg-[var(--background-elevated)] p-5 ${isLeavingPrompt ? "animate-out fade-out zoom-out-95 duration-150 motion-reduce:animate-none" : "animate-in fade-in zoom-in-95 duration-150 motion-reduce:animate-none"}`}
        >
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 ring-1 ring-[var(--accent)]/25">
              <Sparkles
                className="size-5 text-[var(--accent)]"
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">
              <p
                id="update-changelog-prompt-title"
                className="text-base font-semibold text-foreground leading-snug"
              >
                {t("updateChangelogPromptTitle")}
              </p>
              <p className="text-sm text-foreground-soft leading-relaxed">
                {t("updateChangelogPromptBody")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleChangelogNo}
              className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-[var(--muted)] hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              aria-label={t("updateChangelogPromptNo")}
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleChangelogNo}
              className="rounded-full border border-[var(--border-strong)] bg-[var(--muted)] px-4 py-2.5 text-sm font-medium text-foreground transition-[background-color,transform] hover:bg-[var(--border)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {t("updateChangelogPromptNo")}
            </button>
            <button
              type="button"
              onClick={handleChangelogYes}
              className="rounded-full bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--accent-foreground)] transition-[opacity,transform] hover:opacity-90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {t("updateChangelogPromptYes")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!shouldRenderBanner)
    // SEED=2026-09-25-c6c873bf RUN1 FASE4 T6: sin banner y online → null
    // (intacto); offline → región viva sr-only con el aviso localizado.
    return isOffline ? (
      <p className="sr-only" role="status">
        {t("updateOfflinePaused")}
      </p>
    ) : null;

  // 2026-09-25 FASE 3 FIX design-docs-auth: este banner conserva bottom-20
  // como base de la pila — PlanChangeNotice apila ARRIBA en bottom-40 para
  // no solaparse cuando ambos coexisten (mismo z, offset determinista).
  return (
    <div
      data-update-notification
      role="status"
      className={`fixed bottom-20 left-1/2 z-[9999] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 ${isLeavingBanner ? "animate-out fade-out slide-out-to-bottom-1 duration-150 motion-reduce:animate-none" : "animate-in fade-in slide-in-from-bottom-1 duration-150 motion-reduce:animate-none"}`}
    >
      {/* SEED=2026-09-25-c6c873bf RUN1 FASE4 T6: aviso offline solo-SR junto
          al banner (role=status ya presente en el wrapper; este <p> no
          duplica anuncio en online — solo renderiza offline). */}
      {isOffline ? (
        <p className="sr-only" role="status">
          {t("updateOfflinePaused")}
        </p>
      ) : null}
      <div className="lovable-card lovable-card--static modal-surface border-[var(--border-strong)] bg-[var(--background-elevated)]">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 ring-1 ring-[var(--accent)]/25">
            <RefreshCw
              className="size-4 text-[var(--accent)]"
              aria-hidden="true"
            />
          </div>
          <p className="flex-1 text-sm font-medium text-foreground">
            {t("updateAvailableMessage")}
          </p>
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-[var(--muted)] hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            aria-label={t("updateAvailableDismiss")}
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        </div>
        <div className="border-t border-[var(--border)] px-4 py-2.5">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isUpdating}
            className="w-full rounded-full bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--accent-foreground)] transition-[opacity,transform] hover:opacity-90 active:scale-[0.98] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
          >
            {isUpdating
              ? t("updateAvailableUpdating")
              : t("updateAvailableButton")}
          </button>
        </div>
      </div>
    </div>
  );
}
